import styled from '@emotion/styled';
import TipsAndUpdates from '@mui/icons-material/TipsAndUpdates';
import { type Theme, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';

import { Modules } from '../../@types/global';
import VideaBrandSm from '../../common-components/elements/icon/svgs/VideaBrandSm';
import { isDirectorUser } from '../../modules/login/login-utils';
import { usePreferences } from '../../react-query/usePreferences';
import {
  blueBlack400,
  blueBlack500,
  greyLight600,
  orange100,
  orange700,
  white,
} from '../../theme/videa-style-tokens';
import { useIsEmbedded } from '../../utils/hooks';
import { useAppContext } from '../../utils/hooks/useAppContext';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

import { parsePathname } from './path-utils';

const UPDATES_PATH = `https://login.videahealth.io/app/dev-01805549_hubspotprivatecontent_1/exkcozmot4WljD05K5d7/sso/saml`;
const UPDATES_MIN_CALENDAR_DAYS = 12;
const UPDATES_MAX_CALENDAR_DAYS = 19;

export const RenderLogo = ({ color }: { color?: string }) => {
  const { isAuthenticated } = useAppContext();
  return isAuthenticated ? (
    <div className="logo vai-overflow-hidden">
      <VideaBrandSm color={color ?? blueBlack400} />
    </div>
  ) : (
    <></>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'theme' is defined but never used.
const useStyles = makeStyles((theme: Theme) => ({
  updatesIcon: {
    color: orange100,
    transform: 'scale(.67)',
  },
  updates: {
    fontFamily: 'proxima-nova, sans-serif',
    borderRadius: '1.5em',
    padding: '0',
    fontVariantNumeric: 'tabular-nums',
    color: blueBlack500,

    '&.MuiButton-root': {
      width: '1.5em',
      minWidth: '1.5em',
    },
    '& path': {
      transform: 'translateX(.025em)',
    },
    '&:hover': {
      backgroundColor: greyLight600,
      borderColor: greyLight600,
    },
  },
  newUpdates: {
    '& path': {
      fill: orange700,
    },
    backgroundColor: white,
    border: `1.5px solid ${white}`,
  },
}));

export const ProductUpdatesNotifier: React.FC = () => {
  const classes = useStyles();

  const { pathname } = useLocation();
  const { isAuthenticated } = useAppContext();
  const { trackEvent } = useTrackEvent();
  const isEmbedded = useIsEmbedded();

  if (!isAuthenticated) return null;

  const currentDay = format(new Date(), 'dd');

  const isUpdateAvailable =
    parseInt(currentDay) <= UPDATES_MAX_CALENDAR_DAYS &&
    parseInt(currentDay) >= UPDATES_MIN_CALENDAR_DAYS
      ? true
      : false;

  const handleUpdatesClick = () => {
    const { patientId } = parsePathname(pathname);

    trackEvent(mixpanelEvents.productUpdates, {
      patientId,
    });
  };

  return (
    <Box>
      <Button
        className={`app-header-button ${classes.updates} ${
          isUpdateAvailable ? classes.newUpdates : ''
        }`}
        onClick={handleUpdatesClick}
        href={UPDATES_PATH}
        target={isEmbedded ? undefined : '_blank'}
      >
        <TipsAndUpdates className={classes.updatesIcon} />
      </Button>
    </Box>
  );
};
